import { useModal, useUserRoleCapabilities } from '@stores';
import { useMutation } from '@tanstack/react-query';

import { RequestStatus, RequestType } from '@src/utils/enums';
import ActionMenu from '@components/action-menu/ActionMenu';
import ActionMenuButton from '@components/action-menu/ActionMenuButton';

import { DashboardMutations } from '@api/services/dashboard';
import { AuthorizedUserPermissions } from '@api/services/authorized-user/types';
import { useNprRequestHandler } from '@api/services/npr/hooks/useNprRequestHandler';
import { useNonPcnRequestHandler } from '@api/services/non-pcn/hooks/useNonPcnRequestHandler';

import { handleEditRequestAction } from '@pages/Dashboard/dashboard.helpers';
import NewApplicationModal from '@pages/Home/components/modals/NewApplication';
import ProjectOwnerModal from '@pages/common/modals/ProjectOwner';
import RequestAccessModal from '@pages/common/modals/RequestAccess';

import DeleteModal from './RequestDeleteModal';
import FilesModal from '../files-modal/FilesModal';
import WithdrawModal from './RequestWithdrawModal';

import '@forms/components/gridCellRenderers/cellRenderers.scss';
import '@pages/dashboard/dashboard.scss';
import Icon from '@components/icon/Icon';
import {
  mdiArchiveArrowDownOutline as archiveIcon,
  mdiAccountGroup as accessIcon,
  mdiBackspaceOutline as withdrawIcon,
  mdiCancel as cancelIcon,
  mdiFileDocumentPlusOutline as addIcon,
  mdiFileDownloadOutline as downloadIcon,
  mdiFileEyeOutline as viewIcon,
  mdiTextBoxEditOutline as editIcon,
  mdiTextBoxMultipleOutline as documentsIcon,
  mdiFileCancelOutline,
} from '@mdi/js';
import ArchiveModal from './RequestArchiveModal';
import CancelHelpModal from './RequestCancelHelpModal';
import { useCancelHelpInquiryStatusMutation } from '@src/api/services/help/hooks';

const actionIconSize = '1.25rem';

const buildMenuItems = ({
  daNumber,
  district,
  flags,
  handlers,
  id,
  isArchivingDisabled,
  methods,
  openModal,
  permissions,
  projectId,
  projectName,
  requestId,
  requestedPermitType,
  requestStatus,
  requestType,
  version,
  archived,
  isHelpInquiryStatusOpen,
}) => {
  const {
    canEditRequest,
    canViewDocuments,
    canDownloadDraft,
    canAddRequest,
    canCancelRequest,
    canWithdrawRequest,
    canEditAccess,
    canViewRequestOwnership,
    canArchiveRequest,
  } = permissions;

  const { handleDeleteButtonClick, handleWithdraw, handleArchive, handleCancelHelpButtonClick } = handlers;
  const { isViolationComplaint } = flags;

  const handleEditClickProperties = {
    district,
    methods,
    projectId,
    projectName,
    requestId,
    requestStatus,
    requestType,
    version,
  };

  const items = [
    {
      id: 'edit',
      label:
        canEditRequest && !archived ? (
          <>
            <Icon focusable={false} path={editIcon} size={actionIconSize} />
            Edit Request
          </>
        ) : (
          <>
            <Icon focusable={false} path={viewIcon} size={actionIconSize} />
            View Request
          </>
        ),
      onClick: () => handleEditRequestAction({ ...handleEditClickProperties, archived }),
      visible: canEditRequest || !canEditRequest,
    },
    {
      id: 'documents',
      label: (
        <>
          <Icon focusable={false} path={documentsIcon} size={actionIconSize} />
          View Documents
        </>
      ),
      onClick: () => openModal(FilesModal, { id, projectId, requestId, version }),
      visible: canViewDocuments,
    },
    {
      id: 'download',
      label: (
        <>
          <Icon focusable={false} path={downloadIcon} size={actionIconSize} />
          Download Draft Documents
        </>
      ),
      onClick: () => handleEditRequestAction({ ...handleEditClickProperties, downloadPdf: true }),
      visible: canDownloadDraft,
    },
    {
      id: 'createFromExisting',
      label: (
        <>
          <Icon focusable={false} path={addIcon} size={actionIconSize} />
          Add a Request to this Project
        </>
      ),
      onClick: () => openModal(NewApplicationModal, { projectId, projectName, daNumber, fromExistingProject: true }),
      visible: canAddRequest && !isViolationComplaint,
    },
    {
      id: 'access',
      label: (
        <>
          <Icon focusable={false} path={accessIcon} size={actionIconSize} />
          Manage Request Access
        </>
      ),
      onClick: () =>
        openModal(RequestAccessModal, {
          projectName: projectName,
          requestType: requestType,
          requestId: requestId,
          version: version,
        }),
      visible: canEditAccess,
    },
    {
      id: 'owner',
      label: (
        <>
          <Icon focusable={false} path={viewIcon} size={actionIconSize} />
          View Project Owner
        </>
      ),
      onClick: () =>
        openModal(ProjectOwnerModal, {
          projectName,
          requestType,
          requestedPermitType,
          requestId,
          version,
        }),
      visible: canViewRequestOwnership,
    },
    {
      id: 'cancel-help',
      label: (
        <>
          <Icon focusable={false} path={mdiFileCancelOutline} size={actionIconSize} />
          Cancel Help Inquiry
        </>
      ),
      onClick: () => openModal(CancelHelpModal, { cancelClick: handleCancelHelpButtonClick }),
      visible: isHelpInquiryStatusOpen,
    },
    {
      id: 'cancel',
      label: (
        <>
          <Icon focusable={false} path={cancelIcon} size={actionIconSize} />
          Cancel Draft Request
        </>
      ),
      onClick: () => openModal(DeleteModal, { deleteClick: handleDeleteButtonClick, projectName, requestType }),
      visible: canCancelRequest,
    },
    {
      id: 'withdraw',
      label: (
        <>
          <Icon focusable={false} path={withdrawIcon} size={actionIconSize} />
          Withdraw Request
        </>
      ),
      onClick: () => openModal(WithdrawModal, { withdrawClick: handleWithdraw, projectName, requestType }),
      visible: canWithdrawRequest,
    },
    {
      id: 'archive',
      label: (
        <>
          <Icon focusable={false} path={archiveIcon} size={actionIconSize} />
          Archive Request
        </>
      ),
      onClick: () => openModal(ArchiveModal, { archived, onArchive: handleArchive }),
      visible: canArchiveRequest && !isArchivingDisabled && !archived,
    },
    {
      id: 'unarchive',
      label: (
        <>
          <Icon focusable={false} path={archiveIcon} size={actionIconSize} />
          Unarchive Request
        </>
      ),
      onClick: () => openModal(ArchiveModal, { archived, onArchive: handleArchive }),
      visible: canArchiveRequest && archived,
    },
  ];
  return items.filter((item) => item.visible);
};

const RequestActions = ({
  daNumber,
  district,
  id,
  isArchivingDisabled,
  onArchive,
  permission,
  projectId,
  projectName,
  requestedPermitType,
  requestId,
  requestStatus,
  requestType,
  version,
  archived,
  isHelpInquiryStatusOpen,
  helpInquiryId,
}) => {
  const { openModal } = useModal();
  const { fetchNpr } = useNprRequestHandler();
  const { fetchNonPcn } = useNonPcnRequestHandler();
  const capabilities = useUserRoleCapabilities((state) => state.roleCapabilities);

  const { mutate: cancelHelpInquiry } = useCancelHelpInquiryStatusMutation();
  const { mutate: cancelRequest } = useMutation({
    mutationFn: DashboardMutations.cancelRequest,
  });
  const { mutate: withdrawRequest } = useMutation({
    mutationFn: DashboardMutations.withdrawRequest,
  });

  const hasWriteAccess = [AuthorizedUserPermissions.Write, AuthorizedUserPermissions.Owner].includes(permission);
  const isOwner = permission === AuthorizedUserPermissions.Owner;
  const isDraft = requestStatus === RequestStatus.Draft;
  const isVc = requestType === RequestType.ViolationComplaint;
  const isNpr = [RequestType.NoPermitRequired, RequestType.ActivityBasedNoPermitRequired].includes(requestType);
  const isNonPcn = requestType === RequestType.NonPreconstructionNotification;
  const isPermitApplication = requestType === RequestType.PermitApplication;
  const isPermitWithType = isPermitApplication && !!requestedPermitType;

  const canAddRequest = capabilities.createRequest && isOwner && !isVc && projectId;
  const canCancelRequest = capabilities.cancelRequest && isOwner && isDraft;
  const canDownloadDraft = (isDraft && !isNpr && !isNonPcn && !isPermitApplication) || (isDraft && isPermitWithType);
  const canEditAccess = capabilities.manageAccess && isOwner;
  const canViewRequestOwnership = [AuthorizedUserPermissions.Write, AuthorizedUserPermissions.Read].includes(
    permission
  );
  const canEditRequest = capabilities.editRequest && isDraft && (hasWriteAccess || isVc);
  const canArchiveRequest = isOwner || isVc;
  const canViewDocuments = !isDraft;
  const canWithdrawRequest =
    capabilities.withdrawRequest &&
    [
      RequestStatus.Acknowledged,
      RequestStatus.AdditionalInformationRequested,
      RequestStatus.Assigned,
      RequestStatus.Insufficient,
      RequestStatus.ReRouted,
      RequestStatus.Resubmitted,
      RequestStatus.Revision,
      RequestStatus.Submitted,
      RequestStatus.UnderRevision,
    ].includes(requestStatus);

  const handleWithdraw = () => {
    withdrawRequest({ projectId, requestId, version });
  };

  const handleDeleteButtonClick = () => {
    cancelRequest({ projectId, requestId, version });
  };

  const handleArchive = () => {
    onArchive({ requestId, version });
  };

  const handleCancelHelpButtonClick = () => {
    cancelHelpInquiry({ requestId, version, helpInquiryId });
  };

  const flags = {
    isDraft,
    isNpr,
    isViolationComplaint: isVc,
  };

  const handlers = {
    handleDeleteButtonClick,
    handleWithdraw,
    handleArchive,
    handleCancelHelpButtonClick,
  };

  const methods = {
    fetchNpr,
    fetchNonPcn,
  };

  const permissions = {
    canEditRequest,
    canViewDocuments,
    canDownloadDraft,
    canAddRequest,
    canCancelRequest,
    canWithdrawRequest,
    canEditAccess,
    canViewRequestOwnership,
    canArchiveRequest,
  };

  const menuItems = buildMenuItems({
    daNumber,
    district,
    flags,
    handlers,
    id,
    isArchivingDisabled,
    methods,
    openModal,
    permissions,
    projectId,
    projectName,
    requestedPermitType,
    requestId,
    requestStatus,
    requestType,
    version,
    archived,
    isHelpInquiryStatusOpen,
  });

  return (
    <ActionMenu items={menuItems}>
      <ActionMenuButton />
    </ActionMenu>
  );
};
export default RequestActions;
